import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageSquare, Send, Bot, User as UserIcon, ExternalLink, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SupportChatDialog = ({ isOpen, onClose, contextType = 'manual' }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const contextName = contextType === 'manual' ? 'Benutzerhandbuch' : 'FAQ';

  useEffect(() => {
    // Begrüßungsnachricht beim Öffnen
    if (isOpen && messages.length === 0) {
      setMessages([{
        type: 'bot',
        text: `👋 Hallo! Ich bin der CarCortex KI-Assistent für das ${contextName}.\n\nIch helfe dir gerne bei Fragen zur Bedienung und den Features von CarCortex. Wie kann ich dir helfen?`,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, contextName, messages.length]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to chat
    const newUserMessage = {
      type: 'user',
      text: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Build conversation history for context
      const conversationHistory = messages
        .filter(m => m.type === 'user' || m.type === 'bot')
        .map(m => ({
          [m.type === 'user' ? 'user' : 'assistant']: m.text
        }));

      const response = await axios.post(
        `${API}/support/chat`,
        {
          message: userMessage,
          context_type: contextType,
          conversation_history: conversationHistory
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const botMessage = {
        type: 'bot',
        text: response.data.answer,
        sourceLink: response.data.source_link,
        sourcePassage: response.data.source_passage,
        needsUpdate: response.data.needs_update,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      // Show info if content will be added
      if (response.data.needs_update) {
        toast.info('💡 Deine Frage wurde als Verbesserungsvorschlag gespeichert!');
      }

    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        type: 'bot',
        text: '❌ Entschuldigung, es gab einen Fehler. Bitte versuche es erneut oder kontaktiere den Support.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Fehler beim Senden der Nachricht');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl h-[600px] flex flex-col"
        style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
            <MessageSquare className="w-6 h-6" style={{ color: '#1EA0E6' }} />
            CarCortex KI-Chat
            <span className="text-sm font-normal ml-2 px-2 py-1 rounded" style={{ backgroundColor: '#1EA0E620', color: '#7CD4FD' }}>
              {contextName}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 p-4" style={{ backgroundColor: '#0B0F14' }}>
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.type === 'bot' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1EA0E6' }}>
                  <Bot className="w-5 h-5" style={{ color: '#FFFFFF' }} />
                </div>
              )}
              
              <div
                className={`max-w-[80%] p-3 rounded-lg ${
                  message.type === 'user' 
                    ? 'rounded-br-none' 
                    : 'rounded-bl-none'
                }`}
                style={{
                  backgroundColor: message.type === 'user' ? '#1EA0E6' : '#1F2937',
                  color: '#E6EDF3'
                }}
              >
                <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                
                {/* Source Link wenn vorhanden */}
                {message.sourceLink && message.sourcePassage && (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: '#374151' }}>
                    <p className="text-xs mb-2" style={{ color: '#94A3B8' }}>
                      📚 Gefunden im {contextName}:
                    </p>
                    <div className="p-2 rounded text-xs" style={{ backgroundColor: '#0B0F14', color: '#7CD4FD' }}>
                      &ldquo;{message.sourcePassage}&rdquo;
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-2 text-xs"
                      onClick={() => {
                        onClose();
                        // Navigate to source
                        window.location.hash = message.sourceLink;
                      }}
                      style={{ color: '#7CD4FD' }}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Zur Quelle
                    </Button>
                  </div>
                )}

                {/* Verbesserungsvorschlag Indicator */}
                {message.needsUpdate && (
                  <div className="mt-2 flex items-center gap-1 text-xs" style={{ color: '#F59E0B' }}>
                    <Sparkles className="w-3 h-3" />
                    Vorschlag wurde gespeichert
                  </div>
                )}

                <p className="text-xs mt-2 opacity-50">
                  {message.timestamp.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>

              {message.type === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#7CD4FD' }}>
                  <UserIcon className="w-5 h-5" style={{ color: '#0B0F14' }} />
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1EA0E6' }}>
                <Bot className="w-5 h-5" style={{ color: '#FFFFFF' }} />
              </div>
              <div className="p-3 rounded-lg rounded-bl-none" style={{ backgroundColor: '#1F2937' }}>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#7CD4FD', animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#7CD4FD', animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#7CD4FD', animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 border-t" style={{ borderColor: '#1F2937' }}>
          <div className="flex gap-2">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Stelle deine Frage..."
              disabled={isLoading}
              style={{
                backgroundColor: '#0B0F14',
                borderColor: '#1F2937',
                color: '#E6EDF3'
              }}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF' }}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
            💡 Tipp: Ich helfe nur bei Fragen zur CarCortex-Nutzung
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
